import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Input, Row, Select, Space, Typography, Modal, Button } from 'antd';
import { useMutation } from 'react-apollo';
import UPDATE_CAMPAIGN_ORDER_REQUESTOR from '../../../core/GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER_REQUESTOR';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { HasAccess } from '@permify/react-role';

const { Text } = Typography;

const CampaignRequestorDetails = ({ formValues, helpers }) => {
  const {
    childrenOrg,
    loadingChildrenOrgs,
    currentUserOrg,
    selectedAdvertiser,
    fetchChildrenOrgs,
    currentUser,
    currentCampaignOrder,
  } = useCampaignRequestorContext();

  const [advertisers, setAdvertisers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newIONumber, setNewIONumber] = useState('');
  const [updateCampaignOrder] = useMutation(UPDATE_CAMPAIGN_ORDER_REQUESTOR);

  useEffect(() => {
    if (childrenOrg && childrenOrg.length) {
      setAdvertisers(
        childrenOrg.map((org) => ({
          value: org.id,
          label: org.name,
        }))
      );
    } else {
      if (!selectedAdvertiser) return;

      setAdvertisers([
        {
          value: selectedAdvertiser.id,
          label: selectedAdvertiser.name,
        },
      ]);
    }
  }, [childrenOrg, selectedAdvertiser]);

  useEffect(() => {
    if (currentUser && currentUser.permission) {
      const authorized = currentUser.permission.isProcessor;
      setCanEdit(authorized);
    }
  }, [currentUser]);

  useEffect(() => {
    handleFetchAdvertisers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetchAdvertisers = (search = '') => {
    fetchChildrenOrgs({
      variables: {
        where: {
          OR: [{ parentOrg_some: { id: currentUserOrg.id } }, { id: currentUserOrg.id }],
          name_contains: search,
        },
        orderBy: 'name_ASC',
        first: 1500, // TODO: Apply virtual scrolling and pagination
      },
    });
  };

  const handleNameChange = (e) => {
    // Remove special characters in real-time using the helper
    const cleanValue = e.target.value.replace(/[^a-zA-Z0-9\s\-_]/g, '');
    if (cleanValue !== e.target.value) {
      helpers.updateField('name', cleanValue);
    }
  };

  const handleAddNewIONumber = async () => {

    if (!newIONumber.trim()) {
      return;
    }

    // Check for duplicate IO numbers
    const currentAdditional = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];

    const isDuplicate = currentAdditional.some(io => io.value === newIONumber);

    if (isDuplicate) {
      return;
    }

    try {
      // If campaign order exists, persist to database immediately
      if (currentCampaignOrder?.id) {
        await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              additionalIONumbers: {
                create: { value: newIONumber },
              },
            },
          },
        });

        // Update local form values with database-like structure
        const newEntry = {
          value: newIONumber,
          createdAt: new Date().toISOString(),
        };
        const updatedList = [...currentAdditional, newEntry];
        helpers.updateField('additionalIONumbers', updatedList);
        helpers.updateField('IONumber', newIONumber);
      } else {
        // For new campaigns, store in temporary form state (will be saved when campaign is created)
        const newEntry = {
          value: newIONumber,
          createdAt: new Date().toISOString(),
          __isStaged: true,
        };
        const updatedAdditional = [...currentAdditional, newEntry];

        helpers.updateField('additionalIONumbers', updatedAdditional);
        helpers.updateField('IONumber', newIONumber);
      }

      setIsModalVisible(false);
      setNewIONumber('');
    } catch (error) {
      console.error('Error adding IO number:', error);
    }
  };

  const handleIONumberChange = (value) => {
    if (value === 'ADD_NEW_IO_NUMBER') {
      setIsModalVisible(true);
    } else {
      helpers.updateField('IONumber', value);
    }
  };

  // Prepare dropdown options
  const ioNumberSource = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];

  const ioNumberOptions = [
    ...ioNumberSource.map((ioNumber, index) => ({
      label: ioNumber.value,
      value: ioNumber.value,
      key: index + 1,
    })),
    {
      label: (
        <span style={{ color: '#1890ff', cursor: 'pointer' }}>
          + Add New IO Number
        </span>
      ),
      value: 'ADD_NEW_IO_NUMBER',
      key: 'add_new',
    },
  ];

  return (
    <>
      <Card id="requester-details" title="DETAILS">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={20}>
            <Col span={10}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Advertiser</Text>
                <Text type="secondary">The one being promoted in this campaign</Text>
                <Form.Item
                  name="advertiserId"
                  rules={[
                    {
                      required: true,
                      message: 'Please select an advertiser',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select Advertiser"
                    optionFilterProp="children"
                    loading={loadingChildrenOrgs}
                    onSearch={(searchText) => handleFetchAdvertisers(searchText)}
                    filterOption={(input, option) => {
                      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    options={advertisers}
                    disabled={!canEdit}
                    value={formValues.advertiserId}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col span={14}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Campaign Name</Text>
                <Text type="secondary">The name you will identify this campaign by</Text>
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter campaign name',
                    },
                    {
                      pattern: /^[a-zA-Z0-9\s\-_]*$/,
                      message:
                        'Only letters, numbers, spaces, hyphens, and underscores are allowed',
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter campaign name"
                    value={formValues.name}
                    onChange={handleNameChange}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Description</Text>
                <Text type="secondary">Optional: Helps to distinguish your campaign</Text>
                <Form.Item name="description">
                  <Input
                    placeholder="Enter optional description for campaign"
                    value={formValues.description}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>IO Number</Text>
                <Text type="secondary">IO number or account identifier</Text>
                <Form.Item
                  name="IONumber"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter IO Number',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select IO Number"
                    value={formValues.IONumber}
                    onChange={handleIONumberChange}
                    options={ioNumberOptions}
                  />
                </Form.Item>
              </Space>
            </Col>
            <HasAccess roles={['isTrafficker']}>
              {/* <Col span={8}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>Additional IO Numbers</Text>
                  <Text type="secondary">Select most recent IO Number</Text>
                  <Form.Item name="additionalIONumbers">
                    <Select
                      style={{ width: '100%' }}
                      showSearch
                      placeholder="Select Additional IO Numbers"
                      value={(formValues.additionalIONumbers || []).map((t) => t.value)}
                      options={
                        (formValues.additionalIONumbers || []).map((ioNumber, index) => ({
                          label: ioNumber.value,
                          value: ioNumber.value,
                          key: index + 1,
                        }))
                      }
                    />
                  </Form.Item>
                </Space>
              </Col> */}
              <Col span={8}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>Renewal Status</Text>
                  <Text type="secondary">Renewal status of the campaign</Text>
                  <Form.Item name="renewalStatus">
                    <Select
                      style={{ width: '100%' }}
                      showSearch
                      placeholder="Expected to Renew"
                      value={formValues.renewalStatus}
                      options={[
                        {
                          label: 'Expected to Renew',
                          value: 'EXPECTED_TO_RENEW',
                          key: 1,
                        },
                        {
                          label: 'Not Renewing',
                          value: 'NOT_RENEWING',
                          key: 2,
                        },
                      ]}
                    />
                  </Form.Item>
                </Space>
              </Col>
            </HasAccess>
          </Row>
        </Space>
      </Card>
      <Form.Item name="productCode" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="additionalIONumbers" hidden>
        <Input />
      </Form.Item>

      <Modal
        title="Add New IO Number"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsModalVisible(false)}>
            Back
          </Button>,
          <Button key="submit" type="primary" onClick={handleAddNewIONumber}>
            Confirm
          </Button>,
        ]}
      >
        <Input
          placeholder="Enter IO Number"
          value={newIONumber}
          onChange={(e) => setNewIONumber(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default CampaignRequestorDetails;